import React, { useState } from "react";

import EditIcon from "@uui/Edit02";
import {
  isEmpty,
  isEmpty as lodashIsEmpty,
  isEqual,
  orderBy,
  some,
} from "lodash/fp";
import { shallow } from "zustand/shallow";

import styled from "@mui/material/styles/styled";

import {
  DetailContainer,
  DetailRow,
  DetailSubHeading,
  EmptyDetailText,
  SelectableChip,
  SelectableChipsContainer,
} from "@app/analytics/components/reports/styles";
import useSearch from "@app/analytics/MeetingReport/hooks/useSearch";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import {
  appendKeywordSearchPrefix,
  isKeywordSearch,
  removeKeywordSearchPrefix,
} from "@app/analytics/MeetingReport/utils";
import type { TranscriptKeyword, TranscriptSpeaker } from "@app/common/types";

import IconButton from "@rui/IconButton";
import { Row } from "@rui/Stack";

import TranscriptEditSpeakerModal from "./TranscriptEditSpeakerModal";

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: "24px",
  height: "24px",
  padding: 0,
  color: theme.palette.shades.neutral[400],
  "& svg": {
    width: "14px",
    height: "14px",
  },
}));

const sortSpeakers = (speakers: TranscriptSpeaker[]) => {
  if (some("rawTalkTimePercent", speakers)) {
    return orderBy(
      [(speaker) => speaker.rawTalkTimePercent ?? 0, "name"],
      ["desc", "asc"],
      speakers
    );
  }

  return orderBy("name", "asc", speakers);
};

const isKeywordSelected = (searchTerm: string, keywordValue: string) => {
  if (!isEmpty(searchTerm) && isKeywordSearch(searchTerm)) {
    return removeKeywordSearchPrefix(searchTerm) === keywordValue;
  }

  return false;
};

const getSpeakerLabel = (speaker: TranscriptSpeaker) => {
  if (speaker.talkTimePercent) {
    return `${speaker.name} (${speaker.talkTimePercent})`;
  }

  return speaker.name;
};

const WithEmptyFallback = ({
  children,
  emptyText,
  isEmpty,
}: {
  children: React.ReactNode;
  emptyText: string;
  isEmpty: boolean;
}) => {
  if (isEmpty) {
    return <EmptyDetailText>{emptyText}</EmptyDetailText>;
  }

  return <SelectableChipsContainer>{children}</SelectableChipsContainer>;
};

const KeywordsAndSpeakers = ({ shouldUpsell }: { shouldUpsell: boolean }) => {
  const { keywords } = useTranscript();
  const search = useSearch();

  const [editSpeakersOpen, setEditSpeakersOpen] = useState(false);

  const { inEditMode, hasFullEditAccess } = useEditStore((state) => ({
    inEditMode: state.editingSection === "transcript",
    hasFullEditAccess: state.editAccessLevel === "full",
  }));

  // speakers can only be edited when the transcript is NOT being edited
  const enableEditing = !inEditMode && hasFullEditAccess;

  const {
    allSpeakers,
    filteredSpeakers,
    setFilteredSpeakers,
    transcriptAvailable,
  } = useTranscriptStore(
    (state) => ({
      allSpeakers: state.allSpeakers,
      filteredSpeakers: state.filteredSpeakers,
      setFilteredSpeakers: state.setFilteredSpeakers,
      transcriptAvailable: state.transcriptAvailable,
    }),
    shallow
  );

  const handleKeywordSearch = (keyword: TranscriptKeyword) => {
    return () => {
      Events.searchKeyword({ keyword: keyword.value });
      return search.performSearch(appendKeywordSearchPrefix(keyword.value));
    };
  };

  const handleToggleSpeaker = (speaker: TranscriptSpeaker, select: boolean) => {
    if (!transcriptAvailable) return;
    return () => {
      if (select) {
        Events.filterTranscriptBySpeakers();
        setFilteredSpeakers([speaker]);
      } else {
        setFilteredSpeakers(allSpeakers);
      }
    };
  };

  const sortedSpeakers = sortSpeakers(allSpeakers);

  return (
    <>
      <TranscriptEditSpeakerModal
        open={editSpeakersOpen}
        onClose={() => setEditSpeakersOpen(false)}
        inEditMode={inEditMode}
      />
      <DetailContainer>
        <DetailRow direction="column" spacing="4px">
          <DetailSubHeading>Keywords</DetailSubHeading>
          <WithEmptyFallback
            isEmpty={lodashIsEmpty(keywords) || shouldUpsell}
            emptyText={
              shouldUpsell
                ? "Upgrade to view keywords"
                : "No keywords were generated for this meeting"
            }
          >
            {keywords.map((k) => {
              const isSelected = isKeywordSelected(search.term, k.value);
              return (
                <SelectableChip
                  key={k.id}
                  label={k.value}
                  onClick={
                    isSelected
                      ? () => search.clearSearch()
                      : handleKeywordSearch(k)
                  }
                  variant={isSelected ? "filled" : "outlined"}
                  color={isSelected ? "primary" : "default"}
                />
              );
            })}
          </WithEmptyFallback>
        </DetailRow>
        <DetailRow direction="column" spacing="4px">
          <Row variant="left" spacing="4px">
            <DetailSubHeading sx={{ margin: 0 }}>Speakers</DetailSubHeading>
            {enableEditing && (
              <StyledIconButton onClick={() => setEditSpeakersOpen(true)}>
                <EditIcon />
              </StyledIconButton>
            )}
          </Row>
          <WithEmptyFallback
            isEmpty={lodashIsEmpty(allSpeakers) || shouldUpsell}
            emptyText={
              shouldUpsell
                ? "Upgrade to view speakers"
                : "No speakers detected for this meeting"
            }
          >
            {sortedSpeakers.map((s) => {
              // only show a speaker as selected if there is more than one speaker and
              // this is the only speaker selected
              const isSelected =
                sortedSpeakers.length > 1 &&
                filteredSpeakers.length === 1 &&
                Boolean(
                  filteredSpeakers.find((speaker) => isEqual(speaker, s))
                );

              return (
                <SelectableChip
                  key={s.name}
                  label={getSpeakerLabel(s)}
                  onClick={handleToggleSpeaker(s, !isSelected)}
                  variant={isSelected ? "filled" : "outlined"}
                  color={isSelected ? "primary" : "default"}
                />
              );
            })}
          </WithEmptyFallback>
        </DetailRow>
      </DetailContainer>
    </>
  );
};

export default KeywordsAndSpeakers;
